import type { TripItineraryData } from 'tripPlanner/api/types'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import { getTextFromParts } from 'tripPlanner/utils/messageParts'

export const FAILED_CHAT_RECOVERY_PROMPT_PREFIX = 'I am continuing a chat after its last assistant response failed.'
const RECOVERY_JSON_FENCE = '\n```json\n'

export function getFailedChatRecoveryDisplayText(prompt: string): string | undefined {
  if (!prompt.trimStart().startsWith(FAILED_CHAT_RECOVERY_PROMPT_PREFIX)) return undefined
  const jsonFenceIndex = prompt.indexOf(RECOVERY_JSON_FENCE)
  if (jsonFenceIndex < 0) return undefined
  return prompt.slice(0, jsonFenceIndex).trim().replace(/\s+/g, ' ')
}

function placeForRecovery(place: TripItineraryData['stops'][number]['stopPlaceDetail']) {
  if (!place) return undefined
  return {
    placeId: place.placeId,
    displayName: place.displayName,
    formattedAddress: place.formattedAddress,
    lat: place.lat,
    lng: place.lng,
  }
}

function itemForRecovery(item: TripItineraryData['stops'][number]['itemsWithoutDay'][number]) {
  return {
    type: item.type,
    name: item.name,
    emoji: item.emoji,
    iconKey: item.iconKey,
    description: item.description,
    place: placeForRecovery(item.itemPlaceDetail),
    notes: item.notes,
    status: item.status,
    time: item.time,
    isBooked: item.isBooked,
    savedItemData: item.savedItemData,
  }
}

export function buildFailedChatRecoveryPrompt(
  itinerary: TripItineraryData | undefined,
  messages: Array<MessageItem>,
  failedAssistantMessageId: string,
  itineraryUnavailable = false,
): string {
  const failedAssistantIndex = messages.findIndex(message => message.id === failedAssistantMessageId)
  if (failedAssistantIndex < 0) throw new Error('Cannot find the failed assistant message')
  const failedUserIndex = messages.findLastIndex((message, index) => index < failedAssistantIndex && message.role === 'user')
  const failedUserMessage = messages[failedUserIndex]
  if (!failedUserMessage) throw new Error('Cannot recover a failed turn without its user message')

  const recentConversation = messages
    .slice(Math.max(0, failedUserIndex - 12), failedUserIndex)
    .map(message => ({ role: message.role, text: getTextFromParts(message.parts).slice(0, 2000) }))
    .filter(message => message.text.trim().length > 0)

  const itineraryJson = itinerary?.stops.length ? {
    startDate: itinerary.startDate,
    endDate: itinerary.endDate,
    indicativeSearchMonth: itinerary.indicativeSearchMonth,
    indicativeTravellerParty: itinerary.indicativeTravellerParty,
    stops: itinerary.stops.map(stop => ({
      placeName: stop.placeName,
      placeCountry: stop.placeCountry,
      place: placeForRecovery(stop.stopPlaceDetail),
      stopType: stop.stopType,
      startDate: stop.startDate,
      endDate: stop.endDate,
      numberOfNights: stop.numberOfNights,
      indicativeTravellerParty: stop.indicativeTravellerParty,
      days: stop.days.map(day => ({
        dayNumber: day.dayNumber,
        date: day.date,
        title: day.title,
        description: day.description,
        items: day.items.map(itemForRecovery),
      })),
      itemsWithoutDay: stop.itemsWithoutDay.map(itemForRecovery),
      transports: stop.transports?.map(transport => ({
        name: transport.name,
        departurePlaceName: itinerary.stops.find(otherStop => otherStop.id === transport.departureStopId)?.placeName,
        arrivalPlaceName: itinerary.stops.find(otherStop => otherStop.id === transport.arrivalStopId)?.placeName,
        options: transport.options.map(option => ({
          type: option.type,
          name: option.name,
          label: option.label,
          emoji: option.emoji,
          notes: option.notes,
          status: option.status,
          duration: option.duration,
          savedItemData: option.savedItemData,
        })),
      })),
    })),
  } : undefined

  return [
    FAILED_CHAT_RECOVERY_PROMPT_PREFIX,
    ...(itineraryJson ? ['Rebuild the itinerary below in this new chat using the itinerary tools. Preserve the places, dates, days, notes, booking status, and offer references. Do not invent missing details or duplicate entries.'] : []),
    'Then address my unanswered request using the recent conversation for context. If a detail cannot be restored, explain what is missing.',
    '',
    '```json',
    JSON.stringify({
      schemaVersion: 'failed-chat-recovery-v1',
      ...(itineraryJson ? { itinerary: itineraryJson } : {}),
      ...(itineraryUnavailable ? { itineraryUnavailable: true } : {}),
      recentConversation,
      unansweredRequest: getTextFromParts(failedUserMessage.parts),
    }, null, 2),
    '```',
  ].join('\n')
}
